import { Link, useLocation } from 'react-router-dom';
import { Search, MapPin, Briefcase, Home } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

// Filled icon variants for active state
const HomeFilled = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M11.47 3.841a.75.75 0 0 1 1.06 0l8.69 8.69a.75.75 0 1 0 1.06-1.061l-8.689-8.69a2.25 2.25 0 0 0-3.182 0l-8.69 8.69a.75.75 0 1 0 1.061 1.06l8.69-8.689Z" />
    <path d="m12 5.432 8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 0 1-.75-.75v-4.5a.75.75 0 0 0-.75-.75h-3a.75.75 0 0 0-.75.75V21a.75.75 0 0 1-.75.75H5.625a1.875 1.875 0 0 1-1.875-1.875v-6.198a2.29 2.29 0 0 0 .091-.086L12 5.432Z" />
  </svg>
);

const SearchFilled = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M10.5 3.75a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5ZM2.25 10.5a8.25 8.25 0 1 1 14.59 5.28l4.69 4.69a.75.75 0 1 1-1.06 1.06l-4.69-4.69A8.25 8.25 0 0 1 2.25 10.5Z" clipRule="evenodd" />
  </svg>
);

const MapPinFilled = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clipRule="evenodd" />
  </svg>
);

const BriefcaseFilled = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path fillRule="evenodd" d="M7.5 5.25a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3v.205c.933.085 1.857.197 2.774.334 1.454.218 2.476 1.483 2.476 2.917v3.033c0 1.211-.734 2.352-1.936 2.752A24.726 24.726 0 0 1 12 15.75c-2.73 0-5.357-.442-7.814-1.259-1.202-.4-1.936-1.541-1.936-2.752V8.706c0-1.434 1.022-2.7 2.476-2.917A48.814 48.814 0 0 1 7.5 5.455V5.25Zm7.5 0v.09a49.488 49.488 0 0 0-6 0v-.09a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5Zm-3 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" />
    <path d="M3 18.4v-2.796a4.3 4.3 0 0 0 .713.31A26.226 26.226 0 0 0 12 17.25c2.892 0 5.68-.468 8.287-1.335.252-.084.49-.189.713-.311V18.4c0 1.452-1.047 2.728-2.523 2.923-2.12.282-4.282.427-6.477.427a49.19 49.19 0 0 1-6.477-.427C4.047 21.128 3 19.852 3 18.4Z" />
  </svg>
);

export const MobileBottomNav = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  
  // Check if user is an org user (has organization_id)
  const isOrgUser = isAuthenticated && user?.organization_id;
  
  // Check if user has premium access for workspace (non-org)
  const hasPremiumAccess = user?.subscription_plan === 'professional' || 
                           user?.subscription_plan === 'organization';

  const navItems = [
    {
      href: '/',
      label: 'Home',
      icon: Home,
      iconFilled: HomeFilled,
      showAlways: true
    },
    {
      href: '/browse',
      label: 'Search',
      icon: Search,
      iconFilled: SearchFilled,
      showAlways: true
    },
    {
      href: '/map',
      label: 'Map',
      icon: MapPin,
      iconFilled: MapPinFilled,
      showAlways: true
    },
    // Org Workspace - only for org users
    {
      href: '/org/dashboard',
      label: 'Workspace',
      icon: Briefcase,
      iconFilled: BriefcaseFilled,
      showAlways: isOrgUser,
      isWorkspace: true,
      isOrgWorkspace: true
    },
    // Personal Workspace - only for non-org premium users
    {
      href: '/workspace',
      label: 'My Work',
      icon: Briefcase,
      iconFilled: BriefcaseFilled,
      showAlways: isAuthenticated && hasPremiumAccess && !isOrgUser,
      requiresPremium: true,
      isWorkspace: true
    }
  ];

  const visibleItems = navItems.filter(item => item.showAlways);

  // Don't show on certain pages like login/register
  const hiddenPaths = ['/login', '/register', '/forgot-password', '/reset-password'];
  if (hiddenPaths.includes(location.pathname)) {
    return null;
  }

  return (
    <>
      {/* Fixed bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-[9999] md:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-around h-14 px-1">
          {visibleItems.map((item) => {
            const isActive = location.pathname === item.href || 
                            (item.href === '/browse' && location.pathname.startsWith('/case'));
            const Icon = isActive ? item.iconFilled : item.icon;
            
            // For workspace, show lock indicator if not premium
            const showLock = item.requiresPremium && !hasPremiumAccess && isAuthenticated;
            
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full px-1 transition-colors relative",
                  isActive 
                    ? "text-safety-orange-500" 
                    : item.isOrgWorkspace
                    ? "text-blue-600 dark:text-blue-400"
                    : item.isWorkspace
                    ? "text-trust-blue-600 dark:text-trust-blue-400"
                    : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
                )}
                data-testid={item.isOrgWorkspace ? "mobile-nav-workspace" : undefined}
              >
                <div className="relative">
                  <Icon className={cn(
                    "w-5 h-5 mb-0.5",
                    !isActive && "stroke-[1.5px]"
                  )} />
                  {/* Active indicator dot */}
                  {isActive && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-safety-orange-500 rounded-full" />
                  )}
                  {/* Activity dot for workspace (placeholder for future updates) */}
                  {item.isWorkspace && !isActive && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-trust-blue-500 rounded-full opacity-0 data-[has-updates=true]:opacity-100" />
                  )}
                </div>
                <span className={cn(
                  "text-[10px] font-medium leading-tight",
                  isActive && "font-semibold"
                )}>
                  {item.label}
                </span>
                {/* Premium badge for workspace */}
                {showLock && (
                  <span className="absolute top-0.5 right-1/4 text-[7px] bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 px-1 rounded">
                    Pro
                  </span>
                )}
              </Link>
            );
          })}
        </div>
        
        {/* Safe area padding for devices with home indicator */}
        <div className="safe-area-bottom bg-white dark:bg-slate-900" />
      </nav>
    </>
  );
};

export default MobileBottomNav;
